import React, { useState } from "react";
import { Table } from "react-bootstrap";
import { Link, Outlet, useLoaderData, useParams } from "react-router-dom";

import { getTryouts } from "../../lib/api";
import {
    defaultDateFormat,
    defaultTimeFormat,
    isValidDate,
} from "../../lib/utils";
import { IAPIResponse, ITryout } from "../../types/api";

export const loader = async () => {
    return getTryouts();
};

const Tryouts = (): React.JSX.Element | undefined => {
    const params = useParams();

    const { tryouts: loadedList } = useLoaderData() as IAPIResponse & {
        tryouts: ITryout[];
    };

    const [tryoutList, setTryoutList] = useState<ITryout[]>(
        loadedList.filter(t => {
            return t.dates.some(d => new Date() < new Date(d.date));
        }),
    );

    return !params.teamId ? (
        <div className="tryoutsContent" data-testid="tryoutsContent">
            {tryoutList.length > 0 ? (
                <Table striped responsive={"xxl"}>
                    <thead>
                        <tr>
                            <th></th>
                            <th>Date</th>
                            <th>Location</th>
                            <th>Team/Organization</th>
                            <th>Age</th>
                            <th>Description</th>
                            <th>Contact Info</th>
                        </tr>
                    </thead>
                    <tbody>
                        {tryoutList.map(t => {
                            let contactInfo: React.ReactNode[] = [];

                            t.contactName &&
                                contactInfo.push(
                                    <b key="name">{`${t.contactName}`}</b>,
                                );

                            t.contactEmail &&
                                contactInfo.push(
                                    <React.Fragment key="email">
                                        {contactInfo.length !== 0 && <br />}
                                        <a href={`mailto: ${t.contactEmail}`}>
                                            {t.contactEmail}
                                        </a>
                                    </React.Fragment>,
                                );

                            t.contactNumber &&
                                contactInfo.push(
                                    <React.Fragment key="phoneNumber">
                                        {contactInfo.length !== 0 && <br />}
                                        {t.contactNumber}
                                    </React.Fragment>,
                                );

                            t.contactSite &&
                                contactInfo.push(
                                    <React.Fragment key="website">
                                        {contactInfo.length !== 0 && <br />}
                                        <a
                                            href={
                                                /http/.test(t.contactSite)
                                                    ? t.contactSite
                                                    : `http://${t.contactSite}`
                                            }
                                        >
                                            visit website
                                        </a>
                                    </React.Fragment>,
                                );
                            return (
                                <tr key={t.id}>
                                    <td>
                                        <Link
                                            to={`/league-management/tryouts/${t.id}`}
                                        >
                                            edit or delete
                                        </Link>
                                    </td>
                                    <td>
                                        {t.dates.map((d, idx) => (
                                            <React.Fragment
                                                key={`team-${t.id}-tryouts-date-${idx}`}
                                            >
                                                {idx !== 0 && <br />}
                                                <b>
                                                    {defaultDateFormat.format(
                                                        new Date(
                                                            `${d.date}T00:00:00`,
                                                        ),
                                                    )}
                                                </b>{" "}
                                                {defaultTimeFormat.format(
                                                    new Date(
                                                        `${d.date}T${d.startTime}`,
                                                    ),
                                                )}
                                                {d.endTime &&
                                                    ` - ${defaultTimeFormat.format(
                                                        new Date(
                                                            `${d.date}T${d.endTime}`,
                                                        ),
                                                    )}`}
                                            </React.Fragment>
                                        ))}
                                    </td>
                                    <td>
                                        {typeof t.field === "object"
                                            ? t.field?.name
                                            : "No field provided"}
                                    </td>
                                    <td>{t.team}</td>
                                    <td>{t.age}</td>
                                    <td>
                                        {t.description ||
                                            "No description provided"}
                                    </td>
                                    <td>
                                        {contactInfo.length !== 0
                                            ? contactInfo
                                            : "No contact info provided"}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            ) : (
                <h4>No upcoming tryouts</h4>
            )}
        </div>
    ) : (
        <Outlet
            context={{
                setTryoutList: setTryoutList,
                tryout: loadedList.find(
                    t => params.teamId && t.id === parseInt(params.teamId),
                ),
            }}
        />
    );
};

export default Tryouts;
